<script setup lang="ts">
import {
    NavigationMenuContent,
    NavigationMenuIndicator,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuRoot,
    NavigationMenuSub,
    NavigationMenuTrigger,
    NavigationMenuViewport,
} from "reka-ui";
import type { StoreProductCategory } from "@medusajs/types";
import type { CSSProperties } from "vue";
import { getMenuCategories } from "~/shared/lib/menu-categories";

const { data: product_categories } =
    useNuxtData<StoreProductCategory[]>("categories");
const { data: availableCategories } = useNuxtData<string[]>(
    "available-categories",
);

const categoriesTree = computed(() =>
    getMenuCategories(
        product_categories.value,
        availableCategories.value,
    ),
);
const props = defineProps<{
    menu: NavigationMenu;
}>();
const emit = defineEmits<{
    (e: "close"): void;
}>();
const activeCategory = defineModel<StoreProductCategory>();

type CategoryMenuItemSettings = {
    color?: string;
    fontWeight?: string;
    hasBottomSpacing?: boolean;
};

type CategoryWithMenuSettings = StoreProductCategory & {
    metadata?: {
        menuItemSettings?: CategoryMenuItemSettings;
    };
};

type MobileMenuItem = {
    id: string;
    title: string;
    path: string;
    color?: string;
    fontWeight?: string;
    hasBottomSpacing?: boolean;
    children: {
        id: string;
        title: string;
        path: string;
        color?: string;
        fontWeight?: string;
        hasBottomSpacing?: boolean;
    }[];
};

const getCategoryMenuItemSettings = (
    category: StoreProductCategory,
): CategoryMenuItemSettings => {
    const typedCategory = category as CategoryWithMenuSettings;
    return typedCategory.metadata?.menuItemSettings ?? {};
};

const getMenuItemStyle = (
    settings?: CategoryMenuItemSettings,
): CSSProperties => ({
    color: settings?.color ?? "inherit",
    fontWeight: settings?.fontWeight ?? undefined,
});

const getMenuItemSpacingClass = (settings?: CategoryMenuItemSettings) => ({
    "mb-4": settings?.hasBottomSpacing,
});

const mobileMenuItems = computed<MobileMenuItem[]>(() => {
    const categoryItems: MobileMenuItem[] = categoriesTree.value.map(
        (category) => {
            const settings = getCategoryMenuItemSettings(category);

            return {
                id: `category-${category.id}`,
                title: category.name,
                path: `/catalog/${category.handle}`,
                color: settings.color,
                fontWeight: settings.fontWeight,
                hasBottomSpacing: settings.hasBottomSpacing,
                children:
                    category.category_children?.map((subCategory) => {
                        const childSettings =
                            getCategoryMenuItemSettings(subCategory);

                        return {
                            id: `subcategory-${subCategory.id}`,
                            title: subCategory.name,
                            path: `/catalog/${subCategory.handle}`,
                            color: childSettings.color,
                            fontWeight: childSettings.fontWeight,
                            hasBottomSpacing: childSettings.hasBottomSpacing,
                        };
                    }) ?? [],
            };
        },
    );

    return [...categoryItems];
});

const activeMobileItemId = ref<string | null>(null);
const activeMobileItem = computed(() =>
    mobileMenuItems.value.find((item) => item.id === activeMobileItemId.value),
);
const activeTabletItemId = ref<string | null>(null);
const activeTabletItem = computed(() =>
    mobileMenuItems.value.find((item) => item.id === activeTabletItemId.value),
);
const activeDesktopItemId = computed<string>({
    get: () => activeCategory.value?.id ?? "",
    set: (categoryId) => {
        activeCategory.value = categoriesTree.value.find(
            (category) => category.id === categoryId,
        );
    },
});

watch(
    () => activeCategory.value?.id,
    (categoryId) => {
        const responsiveItemId = categoryId
            ? `category-${categoryId}`
            : null;

        activeMobileItemId.value = responsiveItemId;
        activeTabletItemId.value = responsiveItemId;
    },
    { immediate: true },
);

const openMobileSubmenu = (itemId: string) => {
    activeMobileItemId.value = itemId;
};

const closeMobileSubmenu = () => {
    activeMobileItemId.value = null;
};

const closeMenu = () => {
    activeMobileItemId.value = null;
    activeTabletItemId.value = null;
    emit("close");
};

const toggleTabletSubmenu = (itemId: string) => {
    activeTabletItemId.value =
        activeTabletItemId.value === itemId ? null : itemId;
};

const toSentenceCase = (value: string) => {
    if (!value) return "";
    const normalizedValue = value.toLowerCase();
    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};
</script>

<template>
    <div class="sm:hidden w-full overflow-x-hidden overflow-y-auto">
        <div
            class="flex w-[200%] transition-transform duration-300 ease-out"
            :class="activeMobileItem ? '-translate-x-1/2' : 'translate-x-0'"
        >
            <div class="w-1/2 shrink-0">
                <ul class="flex flex-col min-w-[9rem]">
                    <li
                        v-for="item of mobileMenuItems"
                        :key="item.id"
                        class="py-2 text-xl leading-5"
                        :class="getMenuItemSpacingClass(item)"
                    >
                        <button
                            v-if="item.children.length"
                            type="button"
                            class="uppercase w-full cursor-pointer font-semibold text-left flex items-center justify-between gap-4"
                            :style="getMenuItemStyle(item)"
                            @click="openMobileSubmenu(item.id)"
                        >
                            <span>{{ item.title }}</span>
                            <SvgoArrowRight
                                aria-hidden="true"
                                filled
                                class="text-xl !mb-0 shrink-0"
                            />
                        </button>
                        <NuxtLink prefetch-on="interaction"
                            v-else
                            :to="item.path"
                            class="uppercase w-full block cursor-pointer font-semibold"
                            :style="getMenuItemStyle(item)"
                            @click="closeMenu"
                        >
                            {{ item.title }}
                        </NuxtLink>
                    </li>
                </ul>
            </div>

             <div class="w-1/2 shrink-0">
                <ul v-if="activeMobileItem" class="flex flex-col min-w-[9rem]">
                    <li
                        v-for="childItem of activeMobileItem.children"
                        :key="childItem.id"
                        class="py-2 text-xl leading-5"
                        :class="getMenuItemSpacingClass(childItem)"
                    >
                        <NuxtLink prefetch-on="interaction"
                            :to="childItem.path"
                            class="w-full block font-medium"
                            :style="getMenuItemStyle(childItem)"
                            @click="closeMenu"
                        >
                            {{ toSentenceCase(childItem.title) }}
                        </NuxtLink>
                    </li>

                    <li class="py-2 text-xl leading-5">
                        <button
                            type="button"
                            aria-label="Назад"
                            class="w-full cursor-pointer font-medium flex items-center"
                            @click="closeMobileSubmenu"
                        >
                            <SvgoArrowRight
                                aria-hidden="true"
                                filled
                                class="text-xl !mb-0 rotate-180 shrink-0"
                            />
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    </div>

    <div class="hidden sm:flex lg:hidden w-full gap-8 items-start">
        <ul class="flex flex-col w-56 shrink-0">
            <li
                v-for="item of mobileMenuItems"
                :key="item.id"
                class="py-2 text-xl leading-5"
                :class="getMenuItemSpacingClass(item)"
            >
                <div class="flex items-start gap-3">
                    <NuxtLink prefetch-on="interaction"
                        :to="item.path"
                        class="uppercase flex-1 block cursor-pointer font-semibold"
                        :style="getMenuItemStyle(item)"
                        @click="closeMenu"
                    >
                        {{ item.title }}
                    </NuxtLink>
                    <button
                        v-if="item.children.length"
                        type="button"
                        :aria-label="`Открыть раздел ${item.title}`"
                        class="cursor-pointer shrink-0"
                        :aria-expanded="activeTabletItemId === item.id"
                        @click="toggleTabletSubmenu(item.id)"
                    >
                        <SvgoArrowRight
                            aria-hidden="true"
                            filled
                            class="text-xl !mb-0"
                        />
                    </button>
                </div>
            </li>
        </ul>

        <ul
            v-if="activeTabletItem?.children?.length"
            class="flex flex-col min-w-36"
        >
            <li
                v-for="childItem of activeTabletItem.children"
                :key="childItem.id"
                class="py-2 text-xl leading-5"
                :class="getMenuItemSpacingClass(childItem)"
            >
                <NuxtLink prefetch-on="interaction"
                    :to="childItem.path"
                    class="w-full block font-medium"
                    :style="getMenuItemStyle(childItem)"
                    @click="closeMenu"
                >
                    {{ toSentenceCase(childItem.title) }}
                </NuxtLink>
            </li>
        </ul>
    </div>
    <NavigationMenuRoot
        v-model="activeDesktopItemId"
        orientation="vertical"
        class="hidden lg:flex w-full"
    >
        <NavigationMenuList class="flex flex-col min-w-[9rem]">
            <NavigationMenuItem
                v-for="category of categoriesTree"
                :key="category.id"
                :value="category.id"
                class="group"
                :class="
                    getMenuItemSpacingClass(
                        getCategoryMenuItemSettings(category),
                    )
                "
            >
                <NavigationMenuTrigger
                    v-if="category.category_children?.length"
                    class="uppercase main-menu-bar-link relative py-2 text-left font-semibold text-xl leading-5 w-full"
                    :style="
                        getMenuItemStyle(getCategoryMenuItemSettings(category))
                    "
                >
                    <NavigationMenuLink as-child>
                        <NuxtLink prefetch-on="interaction"
                            :to="'/catalog/' + category.handle"
                            class="w-full block"
                            @click="$emit('close')"
                        >
                            {{ category.name }}
                        </NuxtLink>
                    </NavigationMenuLink>
                </NavigationMenuTrigger>
                <NavigationMenuLink
                    as-child
                    v-else
                    class="uppercase block text-left py-2 gap-4 font-semibold text-xl leading-5 w-full"
                    :style="
                        getMenuItemStyle(getCategoryMenuItemSettings(category))
                    "
                >
                    <NuxtLink prefetch-on="interaction"
                        :to="'/catalog/' + category.handle"
                        @click="$emit('close')"
                    >
                        {{ category.name }}
                    </NuxtLink>
                </NavigationMenuLink>
                <NavigationMenuContent
                    v-if="category.category_children?.length"
                    class="pl-28 navigation-menu-content"
                >
                    <NavigationMenuSub>
                        <NavigationMenuList class="flex flex-col min-w-[15rem]">
                            <NavigationMenuItem
                                v-for="subCategory of category.category_children"
                                :value="subCategory.handle"
                                :key="subCategory.id"
                                class="uppercase py-2 items-center gap-4 font-medium text-xl leading-5 flex justify-between w-full"
                                :class="
                                    getMenuItemSpacingClass(
                                        getCategoryMenuItemSettings(
                                            subCategory,
                                        ),
                                    )
                                "
                                :style="
                                    getMenuItemStyle(
                                        getCategoryMenuItemSettings(
                                            subCategory,
                                        ),
                                    )
                                "
                            >
                                <NavigationMenuLink>
                                    <NuxtLink prefetch-on="interaction"
                                        :to="'/catalog/' + subCategory.handle"
                                        @click="$emit('close')"
                                    >
                                        {{ subCategory.name }}
                                    </NuxtLink>
                                </NavigationMenuLink>
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenuSub>
                    <NavigationMenuLink />
                </NavigationMenuContent>
            </NavigationMenuItem>
<!--
            <NavigationMenuItem
                v-for="item of menu"
                :key="item.uiRouterKey"
                class="group"
            >
                <NavigationMenuTrigger
                    v-if="item.type === 'WRAPPER' && item.items?.length"
                    class="uppercase py-2 text-left font-semibold text-xl leading-5 w-full"
                >
                    <NavigationMenuLink as-child>
                        <NuxtLink prefetch-on="interaction"
                            :to="item.path"
                            :style="{
                                color:
                                    item.additionalFields?.color ?? 'inherit',
                            }"
                            class="w-full block"
                            @click="$emit('close')"
                        >
                            {{ item.title }}
                        </NuxtLink>
                    </NavigationMenuLink>
                </NavigationMenuTrigger>
                <NavigationMenuLink
                    as-child
                    v-else
                    class="uppercase block text-left py-2 gap-4 font-semibold text-xl leading-5 w-full"
                >
                    <NuxtLink prefetch-on="interaction"
                        :to="item.path"
                        :style="{
                            color: item.additionalFields?.color ?? 'inherit',
                        }"
                        @click="$emit('close')"
                    >
                        {{ item.title }}
                    </NuxtLink>
                </NavigationMenuLink>
                <NavigationMenuContent
                    v-if="item.items?.length"
                    class="pl-[6rem] navigation-menu-content"
                >
                    <NavigationMenuSub>
                        <NavigationMenuList class="flex flex-col min-w-[9rem]">
                            <NavigationMenuItem
                                v-for="subItem of item.items"
                                :value="subItem.slug"
                                :key="item.uiRouterKey"
                                class="uppercase py-2 items-center gap-4 font-medium text-xl leading-5 flex justify-between w-full"
                            >
                                <NavigationMenuLink>
                                    <NuxtLink prefetch-on="interaction"
                                        :to="subItem.path"
                                        :style="{
                                            color:
                                                item.additionalFields?.color ??
                                                'inherit',
                                        }"
                                        @click="$emit('close')"
                                    >
                                        {{ subItem.title }}
                                    </NuxtLink>
                                </NavigationMenuLink>
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenuSub>
                    <NavigationMenuLink />
                </NavigationMenuContent>
            </NavigationMenuItem> -->

            <NavigationMenuIndicator
                class="absolute pointer-events-none top-0 py-1.5 left-full pl-3 translate-y-[var(--reka-navigation-menu-indicator-position)]"
                data-orientation="vertical"
            >
                <span class="text-2xl"
                    ><SvgoArrowRight aria-hidden="true" filled class="!mb-0"
                /></span>
            </NavigationMenuIndicator>
        </NavigationMenuList>

        <div class="perspective-[2000px]">
            <NavigationMenuViewport
                align="start"
                class="navigation-menu-viewport"
            />
        </div>
    </NavigationMenuRoot>
</template>

<style></style>
